import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getMarketingT } from '../utils/marketingTranslations';
import MarketingLayout from '../components/marketing/MarketingLayout';

function HomePage() {
  const { language } = useApp();
  const mt = getMarketingT(language);
  const isRTL = language === 'ar';
  const ff = isRTL ? 'var(--font-arabic)' : 'var(--font-body)';
  const ffd = isRTL ? 'var(--font-arabic)' : 'var(--font-display)';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const FEATURES = [
    { icon: '📱', title: mt('feat_qr_title'), desc: mt('feat_qr_desc') },
    { icon: '🌍', title: mt('feat_lang_title'), desc: mt('feat_lang_desc') },
    { icon: '📲', title: mt('feat_wa_title'), desc: mt('feat_wa_desc') },
    { icon: '🎨', title: mt('feat_brand_title'), desc: mt('feat_brand_desc') },
    { icon: '⚡', title: mt('feat_fast_title'), desc: mt('feat_fast_desc') },
    { icon: '🌙', title: mt('feat_dark_title'), desc: mt('feat_dark_desc') },
  ];

  const STEPS = [
    { num: '01', title: mt('step1_title'), desc: mt('step1_desc') },
    { num: '02', title: mt('step2_title'), desc: mt('step2_desc') },
    { num: '03', title: mt('step3_title'), desc: mt('step3_desc') },
  ];

  return (
    <MarketingLayout>
      <style>{`
        .mk-hero-grid { display: flex; align-items: center; gap: 80px; max-width: 1200px; margin: 0 auto; }
        .mk-features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 1200px; margin: 0 auto; }
        .mk-steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 1200px; margin: 0 auto; }
        .mk-feature-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); border-color: transparent; }
        .mk-step-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
        .mk-cta-link:hover { transform: translateY(-2px); box-shadow: 0 8px 32px var(--accent-glow); }
        .mk-cta2-link:hover { border-color: var(--text-primary); transform: translateY(-2px); }
        @media (max-width: 900px) {
          .mk-hero-grid { flex-direction: column !important; text-align: center; }
          .mk-phone-wrapper { display: none !important; }
          .mk-hero-ctas { justify-content: center !important; }
          .mk-social-proof { justify-content: center !important; }
        }
        @media (max-width: 768px) {
          .mk-features-grid { grid-template-columns: 1fr !important; }
          .mk-steps-grid { grid-template-columns: 1fr !important; }
          .mk-stats-row { gap: 40px !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .mk-features-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ═══ HERO ═══ */}
      <section style={{ padding: '140px 24px 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -200, [isRTL ? 'left' : 'right']: -200,
          width: 600, height: 600, borderRadius: '50%',
          background: 'radial-gradient(circle, var(--accent-light), transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div className="mk-hero-grid">
          <div style={{ flex: 1 }}>
            {/* Badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 16px', borderRadius: 100,
              background: 'var(--accent-light)', color: 'var(--accent)',
              fontSize: 13, fontWeight: 600,
            }}>
              <span>🚀</span> {mt('hero_badge')}
            </div>

            {/* Title */}
            <h1 style={{
              fontFamily: ffd, fontSize: 'clamp(36px, 6vw, 68px)', fontWeight: 700,
              lineHeight: 1.1, color: 'var(--text-primary)', letterSpacing: isRTL ? '0' : '-0.03em',
              maxWidth: 700, marginTop: 24,
            }}>
              {mt('hero_title_1')}<br />
              {mt('hero_title_2')}{' '}
              <span style={{ color: 'var(--accent)' }}>{mt('hero_title_3')}</span>
            </h1>

            {/* Subtitle */}
            <p style={{
              fontSize: 18, color: 'var(--text-secondary)', lineHeight: 1.7,
              maxWidth: 520, marginTop: 24,
            }}>
              {mt('hero_sub')}
            </p>

            {/* CTAs */}
            <div className="mk-hero-ctas" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 36 }}>
              <Link to="/contact" className="mk-cta-link" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '16px 36px', background: 'var(--accent)', color: 'var(--text-inverse)',
                borderRadius: 'var(--radius-md)', textDecoration: 'none',
                fontSize: 16, fontWeight: 600, boxShadow: '0 4px 24px var(--accent-glow)',
                transition: 'all 0.3s',
              }}>
                {mt('hero_cta')}
              </Link>
              <Link to="/r/casa-bella" className="mk-cta2-link" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '16px 36px', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                border: '2px solid var(--border)', borderRadius: 'var(--radius-md)',
                textDecoration: 'none', fontSize: 16, fontWeight: 600, transition: 'all 0.3s',
              }}>
                {mt('hero_cta_demo')}
              </Link>
            </div>

            {/* Social proof */}
            <div className="mk-social-proof" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 32 }}>
              <div style={{ display: 'flex' }}>
                {['🍕', '🍣', '🥘', '☕'].map((e, i) => (
                  <div key={i} style={{
                    width: 36, height: 36, borderRadius: '50%',
                    border: '3px solid var(--bg-primary)',
                    [isRTL ? 'marginRight' : 'marginLeft']: i > 0 ? -10 : 0,
                    background: ['#F2994A', '#6C63FF', '#27AE60', '#EB5757'][i],
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                  }}>{e}</div>
                ))}
              </div>
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                {mt('hero_trusted')} <strong style={{ color: 'var(--text-primary)' }}>50+</strong> {mt('hero_restaurants')}
              </span>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="mk-phone-wrapper" style={{ flexShrink: 0 }}>
            <div style={{
              width: 280, height: 560, borderRadius: 40,
              background: 'var(--bg-tertiary)', padding: 12,
              boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border)',
            }}>
              <div style={{
                width: '100%', height: '100%', borderRadius: 30,
                background: 'var(--bg-primary)',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: 16, overflow: 'hidden',
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Casa Bella</div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>🔍 Search menu...</div>
                {['🍕', '🥗', '🍝', '🍰'].map((emoji, i) => (
                  <div key={i} style={{
                    width: '80%', padding: '12px 16px',
                    background: 'var(--bg-secondary)', borderRadius: 12,
                    boxShadow: 'var(--shadow-sm)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: 'var(--accent-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                    }}>{emoji}</div>
                    <div>
                      <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', marginBottom: 6, width: [80, 70, 90, 60][i] }} />
                      <div style={{ height: 6, borderRadius: 3, background: 'var(--border-light)', width: [50, 40, 55, 45][i] }} />
                    </div>
                  </div>
                ))}
                <div style={{
                  marginTop: 8, padding: '10px 32px',
                  background: 'var(--accent)', borderRadius: 10,
                  color: 'var(--text-inverse)', fontSize: 11, fontWeight: 600,
                }}>Order via WhatsApp</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section style={{ padding: '80px 24px', background: 'var(--bg-secondary)' }}>
        <div style={{ textAlign: 'center', marginBottom: 64, maxWidth: 1200, margin: '0 auto 64px' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
            {mt('features_label')}
          </p>
          <h2 style={{ fontFamily: ffd, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: 'var(--text-primary)' }}>
            {mt('features_title')}
          </h2>
        </div>
        <div className="mk-features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="mk-feature-card" style={{
              padding: 32, borderRadius: 'var(--radius-lg)',
              background: 'var(--bg-primary)', border: '1px solid var(--border-light)',
              transition: 'all 0.3s',
            }}>
              <div style={{ fontSize: 32, marginBottom: 16 }}>{f.icon}</div>
              <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, fontFamily: ff }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 64, maxWidth: 1200, margin: '0 auto 64px' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
            {mt('how_label')}
          </p>
          <h2 style={{ fontFamily: ffd, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: 'var(--text-primary)' }}>
            {mt('how_title')}
          </h2>
        </div>
        <div className="mk-steps-grid">
          {STEPS.map((s, i) => (
            <div key={i} className="mk-step-card" style={{
              padding: '40px 32px', borderRadius: 'var(--radius-lg)',
              background: 'var(--bg-secondary)', border: '1px solid var(--border-light)',
              position: 'relative', overflow: 'hidden', transition: 'all 0.3s',
            }}>
              <span style={{
                fontFamily: 'var(--font-display)', fontSize: 64, fontWeight: 700,
                color: 'var(--bg-tertiary)', position: 'absolute', top: -8,
                [isRTL ? 'left' : 'right']: 16, lineHeight: 1,
              }}>{s.num}</span>
              <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12, position: 'relative', fontFamily: ff }}>
                {s.title}
              </h3>
              <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.7, position: 'relative' }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section style={{ padding: '60px 24px', background: 'var(--bg-secondary)' }}>
        <div className="mk-stats-row" style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'flex', justifyContent: 'center', gap: 80, flexWrap: 'wrap',
        }}>
          {[
            { num: '50+', label: mt('stat_restaurants') },
            { num: '0%', label: mt('stat_commission') },
            { num: '< 1s', label: mt('stat_load') },
            { num: '3', label: mt('stat_languages') },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: ffd, fontSize: 44, fontWeight: 700, color: 'var(--accent)' }}>{s.num}</div>
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: '80px 48px', textAlign: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'radial-gradient(circle at 30% 50%, var(--accent-light), transparent 60%)',
              pointerEvents: 'none',
            }} />
            <h2 style={{ fontFamily: ffd, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, position: 'relative' }}>
              {mt('cta_title')}
            </h2>
            <p style={{ fontSize: 16, color: 'var(--text-muted)', lineHeight: 1.7, maxWidth: 480, margin: '0 auto 36px', position: 'relative' }}>
              {mt('cta_desc')}
            </p>
            <Link to="/contact" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '16px 40px', background: 'var(--accent)', color: 'var(--text-inverse)',
              borderRadius: 'var(--radius-md)', textDecoration: 'none',
              fontSize: 16, fontWeight: 600, boxShadow: '0 4px 24px var(--accent-glow)',
              position: 'relative',
            }}>
              {mt('cta_btn')}
            </Link>
          </div>
        </div>
      </section>

      <div style={{ height: 40 }} />
    </MarketingLayout>
  );
}

export default HomePage;